/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, Input, NgZone, OnChanges, SimpleChanges} from '@angular/core';
import * as _ from 'lodash';

import {formatNoteData} from './notes-commons';
import {NoteResource} from '../../../../components/api/resources/resource/note.resource';
import {UserResource} from '../../../../components/api/resources/resource/user.resource';
import {LocalUserProfile} from '../../../../components/session/local-user-profile';

interface NoteEntry {
  id: string;
  data?: string | string[];
  updateTime?: string;
  updateUser?: {id: string; displayName?: string; name?: string};
  createUser?: {id: string; displayName?: string; name?: string};
}

@Component({
  standalone: false,
  selector: 'adn-object-notes-button',
  template: `
    <ng-template #popTemplate>
      <div class="adn-notes adn-notes-popover">
        <p class="sectionTitle"><strong>Notes</strong></p>
        <p class="alert alertSimple" *ngIf="adminUserName">These notes are only visible to <code>{{ adminUserName }}</code>.</p>
        <p class="alert alertSimple" *ngIf="!loading && !notes.length">No notes available</p>
        <p class="alert alertSimple" *ngIf="loading"><span class="fa fa-cog fa-spin"></span></p>
        <ul class="list-group timestamped" *ngIf="notes.length">
          <li class="list-group-item" *ngFor="let note of sortedNotes">
            <span class="timestamp text-muted small">
              <span *ngIf="note.updateUser?.displayName || note.updateUser?.name">{{ note.updateUser.displayName || note.updateUser.name }},&nbsp;</span>
              {{ note.updateTime | localeDate:'short' }}
            </span>
            <div class="note-content" [innerHTML]="formatNoteData(note.data) | safeHtml"></div>
          </li>
        </ul>
      </div>
    </ng-template>
    <button *ngIf="hasNotes"
            type="button"
            class="infoIconLink btn btn-link btn-icon-only"
            [title]="title"
            [outsideClick]="true"
            [placement]="placement"
            containerClass="adn-object-notes-popover"
            [popover]="popTemplate"
            (onShown)="onPopoverShown()">
      <span class="fa fa-fw fa-info-circle"></span>
    </button>
  `,
  styles: [`
    .adn-notes-popover {
      max-height: 320px;
      overflow-y: auto;
      overflow-x: hidden;
    }

    .adn-notes-popover .sectionTitle {
      margin-top: 0;
      padding-top: 0;
      margin-bottom: 0;
      font-size: 11px;
      font-weight: bold;
      line-height: 1.2;
      text-transform: uppercase;
    }

    .adn-notes-popover .list-group {
      margin-bottom: 0;
    }

    .adn-notes-popover .timestamp {
      display: block;
      margin-bottom: 6px;
    }

    .adn-notes-popover .note-content {
      display: block;
    }
  `]
})
export class ObjectNotesButtonComponent implements OnChanges {
  @Input() object: any;
  @Input() param = 'notes';
  @Input() adminUserName: string;
  @Input() title = 'View notes';
  @Input() placement = 'bottom';

  notes: NoteEntry[] = [];
  loading = false;
  private loaded = false;
  private localUser: any;

  readonly formatNoteData = formatNoteData;

  constructor(
    @Inject(NoteResource) private Note: any,
    @Inject(LocalUserProfile) private localUserProfile: any,
    @Inject(UserResource) private User: any,
    private ngZone: NgZone
  ) {
  }

  get hasNotes(): boolean {
    return Array.isArray(this.object?.[this.param]) && this.object[this.param].length > 0;
  }

  get sortedNotes(): NoteEntry[] {
    return _.orderBy(this.notes, ['updateTime'], ['desc']);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.object || changes.param) {
      this.notes = [];
      this.loading = false;
      this.loaded = false;
    }
  }

  onPopoverShown(): void {
    if (this.loaded || !this.hasNotes) {
      return;
    }
    this.loadNotes();
  }

  private loadNotes(): void {
    this.loading = true;

    this.Note.query(_.map(this.object[this.param], 'id'), (page: {results: NoteEntry[]}) => {
      this.ngZone.run(() => {
        this.notes = page.results || [];
        this.loading = false;
        this.loaded = true;
        this.resolveNoteAuthors();
      });
    });
  }

  private resolveNoteAuthors(): void {
    if (!this.localUser) {
      this.localUser = this.localUserProfile.get();
    }

    const userIds = _.map(this.notes, (note) => {
      const safeUser = note.updateUser || note.createUser;
      return safeUser?.id;
    });
    const searchUserIds = _.without(userIds, this.localUser.userId);
    const userPromise = searchUserIds.length ? this.User.query(searchUserIds).$promise : Promise.resolve({results: []});

    Promise.resolve(userPromise).then((page: {results: any[]}) => {
      this.ngZone.run(() => {
        const users = _.keyBy(page.results, 'id');
        _.forEach(this.notes, (note) => {
          const safeUser = note.updateUser || note.createUser;
          if (!safeUser) {
            return;
          }
          if (safeUser.id === this.localUser.userId) {
            _.set(note, ['updateUser', 'displayName'], this.localUser.displayName);
            _.set(note, ['updateUser', 'name'], this.localUser.name);
            return;
          }
          const foundUser = users[safeUser.id];
          if (foundUser) {
            _.set(note, ['updateUser', 'displayName'], foundUser.displayName);
            _.set(note, ['updateUser', 'name'], foundUser.name);
          }
        });
      });
    });
  }
}
